<script setup lang="ts">
import { computed, ref } from "vue";
import { responsiveVariables } from "@magnit/core/src/utilities/helpers";
import VText from "@magnit/core/src/components/VText/VText.vue";
import VYaSlot from "@magnit/core/src/containers/VYaSlot/VYaSlot.vue";
import type { IYaSlotConfig } from "@magnit/core/src/containers/VYaSlot/VYaSlot.types";

interface ICatalogHeaderProps {
  title: string;
  count: number;
  image?: string;
  yaSlotConfig?: IYaSlotConfig | IYaSlotConfig[];
  yaSlotDisclaimer?: string;
}

const props = defineProps<ICatalogHeaderProps>();
const slotApi = useSlotsStore();
const { adfoxOwnerId } = usePublicConfig();
const stub = ref(false);
const yaslotRendered = ref(false);
const yaslotError = ref(false);

const rootVariables = computed(() => {
  let image = props.image || "/images/catalog-header/default.webp";
  image = `url(${image})`;

  return {
    ...responsiveVariables("--_bg", image),
  };
});
</script>

<template>
  <section
    class="catalog-header"
    :class="{ 'catalog-header_yaslot-rendered': yaslotRendered }"
    :style="!yaSlotConfig || stub || yaslotError ? rootVariables : ''"
  >
    <div
      v-if="yaSlotConfig && !stub && !yaslotError"
      class="catalog-header__yaslot"
    >
      <VYaSlot
        :api-ready="slotApi.slotStatus === IYaSlotApiStatus.SUCCESS"
        :api-error="slotApi.slotStatus === IYaSlotApiStatus.ERROR"
        :owner-id="adfoxOwnerId"
        :config="yaSlotConfig"
        :disclaimer="yaSlotDisclaimer"
        with-hover
        @stub="stub = true"
        @render="yaslotRendered = true"
        @error="yaslotError = true"
      />
    </div>
    <h1 class="catalog-header__title">
      {{ title }}
    </h1>
    <VText
      color="invert-primary"
      font="body-large-accent"
      class="catalog-header__subtitle"
    >
      {{ count }}
      {{ plural(count, "товар", "товара", "товаров") }}
    </VText>
  </section>
</template>

<style scoped lang="postcss">
.catalog-header {
  --_bg: unset;

  position: relative;
  overflow: hidden;
  width: 100%;
  height: 150px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  background: var(--pl-surface-intense-disabled);
  background-image: var(--_bg);
  background-position: center;
  background-size: cover;

  &_yaslot-rendered {
    background: none;
  }

  &__title {
    font-family: var(--font-family-secondary);
    font-weight: 400;
    font-size: 24px;
    line-height: 1.1em;
    margin-bottom: 4px;
    color: var(--pl-text-invert-primary);
    position: relative;
    z-index: var(--pl-z-index-level2);
  }

  &__subtitle {
    position: relative;
    z-index: var(--pl-z-index-level2);
  }

  &__yaslot {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;

    &:deep(.yaslot-wrapper) {
      width: 100%;
      height: 100%;
      overflow: hidden;
      justify-content: center;

      .disclaimer {
        bottom: var(--pl-unit-x1);
        right: var(--pl-unit-x5);
      }

      .yaslot-item {
        height: 100%;

        a {
          height: 100%;

          img {
            height: 100% !important;
            object-fit: cover;
            object-position: center;
          }
        }
      }
    }
  }
}

@media (--pl-viewport-from-m) {
  .catalog-header {
    height: 200px;

    &__title {
      font-size: 36px;
    }
  }
}

@media (--pl-viewport-from-ml) {
  .catalog-header {
    height: 320px;
  }
}
</style>
